import * as i1$2 from '@angular/cdk/stepper';
import { CdkStep, CdkStepper, CdkStepperNext, CdkStepperPrevious, CdkStepHeader, CdkStepperModule } from '@angular/cdk/stepper';
import * as i0 from '@angular/core';
import { forwardRef, Component, ChangeDetectionStrategy, Inject, SkipSelf, QueryList, Directive, Optional, ContentChildren, Input, ViewChildren, NgModule } from '@angular/core';
import * as i1 from '@aposin/ng-aquila/utils';
import { ErrorStateMatcher, clamp } from '@aposin/ng-aquila/utils';
import { Subject } from 'rxjs';
import { takeWhile, takeUntil } from 'rxjs/operators';
import * as i1$1 from '@angular/cdk/bidi';
import { NgClass, NgTemplateOutlet, CommonModule } from '@angular/common';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2 from '@aposin/ng-aquila/icon';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import * as i1$3 from '@angular/cdk/a11y';
import * as i2$1 from '@aposin/ng-aquila/progressbar';
import { NxProgressbarModule } from '@aposin/ng-aquila/progressbar';
import { NxLabelModule } from '@aposin/ng-aquila/base';

// We need to reference steps in stepper and stepper in steps. To prevent circular depenedency errors
// Provide both components in a single file. Otherwise we would have to introduce interface/abstract classes
// shared between both implementations.
const _c0 = ["*"];
function NxStepComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NxMultiStepperGroupComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NxMultiStepItemComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 2);
  }
}
function NxMultiStepItemComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nx-icon", 3);
  }
}
function NxMultiStepItemComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("label", ctx_r0.label);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.label);
  }
}
function NxMultiStepItemComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 5);
  }
}
const _c1 = [[["nx-label"]]];
const _c2 = ["nx-label"];
const _c3 = (a0, a1) => ({
  "is-disabled": a0,
  "is-selected": a1
});
function NxMultiStepperComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 0);
    i0.ɵɵprojection(1);
    i0.ɵɵelementEnd();
  }
}
function NxMultiStepperComponent_Conditional_2_For_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nx-multi-step-item", 6);
    i0.ɵɵlistener("click", function NxMultiStepperComponent_Conditional_2_For_1_Template_nx_multi_step_item_click_0_listener() {
      const ctx_r1 = i0.ɵɵrestoreView(_r1);
      const step_r3 = ctx_r1.$implicit;
      const ɵ$index_8_r4 = ctx_r1.$index;
      const ctx_r4 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(!ctx_r4._stepDisabled(ɵ$index_8_r4) && step_r3.select());
    })("keydown", function NxMultiStepperComponent_Conditional_2_For_1_Template_nx_multi_step_item_keydown_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r4 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r4._onKeydown($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r3 = ctx.$implicit;
    const ɵ$index_8_r4 = ctx.$index;
    const ɵ$count_8_r6 = ctx.$count;
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("last", ɵ$index_8_r4 === ɵ$count_8_r6 - 1)("tabindex", ctx_r4._getFocusIndex() === ɵ$index_8_r4 ? 0 : -1)("label", step_r3.stepLabel || step_r3.label)("completed", step_r3.completed || step_r3.wasCompleted)("selected", ctx_r4.selectedIndex === ɵ$index_8_r4)("active", ctx_r4.selectedIndex > ɵ$index_8_r4)("disabled", ctx_r4._stepDisabled(ɵ$index_8_r4))("direction", ctx_r4.direction)("ariaControls", ctx_r4._getStepContentId(ɵ$index_8_r4))("wasCompleted", step_r3.wasCompleted);
  }
}
function NxMultiStepperComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, NxMultiStepperComponent_Conditional_2_For_1_Template, 1, 10, "nx-multi-step-item", 5, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(ctx_r4.steps);
  }
}
function NxMultiStepperComponent_Conditional_3_For_1_For_5_Template(rf, ctx) {
  if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "nx-multi-step-item", 11);
    i0.ɵɵlistener("click", function NxMultiStepperComponent_Conditional_3_For_1_For_5_Template_nx_multi_step_item_click_0_listener() {
      const step_r8 = i0.ɵɵrestoreView(_r7).$implicit;
      const ctx_r4 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(!ctx_r4._stepDisabled(ctx_r4._getIndex(step_r8)) && step_r8.select());
    })("keydown", function NxMultiStepperComponent_Conditional_3_For_1_For_5_Template_nx_multi_step_item_keydown_0_listener($event) {
      i0.ɵɵrestoreView(_r7);
      const ctx_r4 = i0.ɵɵnextContext(3);
      return i0.ɵɵresetView(ctx_r4._onKeydown($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r8 = ctx.$implicit;
    const ɵ$index_20_r9 = ctx.$index;
    const ɵ$count_20_r10 = ctx.$count;
    const ɵ$index_12_r11 = i0.ɵɵnextContext().$index;
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("tabindex", ctx_r4._getFocusIndex() === ctx_r4._getIndex(step_r8) ? 0 : -1)("last", ɵ$index_20_r9 === ɵ$count_20_r10 - 1)("label", step_r8.stepLabel || step_r8.label)("completed", step_r8.completed || step_r8.wasCompleted)("selected", ctx_r4.selectedIndex === ctx_r4._getIndex(step_r8))("active", ctx_r4.selectedIndex > ctx_r4._getIndex(step_r8))("disabled", ctx_r4._stepDisabled(ctx_r4._getIndex(step_r8)))("direction", ctx_r4.direction)("wasCompleted", step_r8.wasCompleted)("ariaControls", ctx_r4._getStepContentId(ɵ$index_12_r11));
  }
}
function NxMultiStepperComponent_Conditional_3_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7)(1, "div", 8);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 9);
    i0.ɵɵrepeaterCreate(4, NxMultiStepperComponent_Conditional_3_For_1_For_5_Template, 1, 10, "nx-multi-step-item", 10, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const group_r12 = ctx.$implicit;
    const ɵ$index_12_r11 = ctx.$index;
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵclassProp("is-active", ctx_r4._isGroupActive(ɵ$index_12_r11))("is-disabled", ctx_r4._isGroupDisabled(ɵ$index_12_r11));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", group_r12.label, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵrepeater(group_r12.steps);
  }
}
function NxMultiStepperComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, NxMultiStepperComponent_Conditional_3_For_1_Template, 6, 5, "div", 7, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(ctx_r4.groups);
  }
}
function NxMultiStepperComponent_Conditional_4_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 13);
    i0.ɵɵlistener("click", function NxMultiStepperComponent_Conditional_4_For_2_Template_div_click_0_listener() {
      const ctx_r13 = i0.ɵɵrestoreView(_r13);
      const step_r15 = ctx_r13.$implicit;
      const ɵ$index_26_r16 = ctx_r13.$index;
      const ctx_r4 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(!ctx_r4._stepDisabled(ɵ$index_26_r16) && step_r15.select());
    });
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const step_r15 = ctx.$implicit;
    const ɵ$index_26_r16 = ctx.$index;
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c3, ctx_r4._stepDisabled(ɵ$index_26_r16), ctx_r4.selectedIndex === ɵ$index_26_r16));
    i0.ɵɵattribute("aria-controls", ctx_r4._getStepContentId(ɵ$index_26_r16));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(step_r15.label);
  }
}
function NxMultiStepperComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵrepeaterCreate(1, NxMultiStepperComponent_Conditional_4_For_2_Template, 3, 6, "div", 12, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵrepeater(ctx_r4.steps);
  }
}
function NxMultiStepperComponent_For_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵelementContainer(1, 14);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r17 = ctx.$implicit;
    const ɵ$index_35_r18 = ctx.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", ctx_r4._getStepContentId(ɵ$index_35_r18));
    i0.ɵɵattribute("aria-label", ctx_r4.currentLabel)("aria-expanded", ctx_r4.selectedIndex === ɵ$index_35_r18);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", step_r17.content);
  }
}
function NxProgressStepperComponent_For_4_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 9);
    i0.ɵɵelement(1, "nx-progressbar", 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("value", ctx_r0.progress)("ariaLabel", ctx_r0.progressbarAriaLabel)("ariaLabelledBy", ctx_r0.progressbarAriaLabeledBy);
  }
}
function NxProgressStepperComponent_For_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5)(1, "div", 6)(2, "div", 7);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(4, "div", 8);
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(6, NxProgressStepperComponent_For_4_Conditional_6_Template, 2, 3, "div", 9);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r2 = ctx.$implicit;
    const ɵ$index_6_r3 = ctx.$index;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("nx-subprogress__item--inactive", ctx_r0.selectedIndex !== ɵ$index_6_r3 && !step_r2.completed)("nx-subprogress__item--selected", ctx_r0.selectedIndex === ɵ$index_6_r3);
    i0.ɵɵattribute("aria-disabled", ctx_r0.selectedIndex !== ɵ$index_6_r3 && !step_r2.completed);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", ɵ$index_6_r3 + 1, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(step_r2.stepLabel || step_r2.label);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.selectedIndex === ɵ$index_6_r3 ? 6 : -1);
  }
}
function NxProgressStepperComponent_For_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵelementContainer(1, 11);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r4 = ctx.$implicit;
    const ɵ$index_25_r5 = ctx.$index;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", ctx_r0._getStepContentId(ɵ$index_25_r5));
    i0.ɵɵattribute("aria-label", ctx_r0.currentLabel)("aria-expanded", ctx_r0.selectedIndex === ɵ$index_25_r5);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", step_r4.content);
  }
}
const _c4 = "[_nghost-%COMP%]{display:block}.nx-progress-stepper__content[aria-expanded=false][_ngcontent-%COMP%]{height:0;overflow:hidden;visibility:hidden}.nx-progress-stepper__mobile-label[_ngcontent-%COMP%]{display:none;color:var(--progress-indicator-mobile-color);font-size:var(--progress-indicator-mobile-font-size);line-height:var(--progress-indicator-mobile-line-height);font-weight:var(--progress-indicator-mobile-font-weight);letter-spacing:var(--progress-indicator-mobile-letter-spacing)}@media (max-width: 703px){.nx-progress-stepper__mobile-label[_ngcontent-%COMP%]{display:block;text-align:center}}.nx-progress-stepper__title[_ngcontent-%COMP%]{display:block}.nx-progress-stepper__title[_ngcontent-%COMP%]     nx-label{margin-bottom:24px}";
function NxSingleStepperComponent_For_12_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7);
    i0.ɵɵelementContainer(1, 8);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const step_r1 = ctx.$implicit;
    const ɵ$index_19_r2 = ctx.$index;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", ctx_r2._getStepContentId(ɵ$index_19_r2));
    i0.ɵɵattribute("aria-label", ctx_r2.currentLabel)("aria-expanded", ctx_r2.selectedIndex === ɵ$index_19_r2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", step_r1.content);
  }
}
class NxStepComponent extends CdkStep {
  constructor(stepper, _errorStateMatcher) {
    super(stepper);
    this.stepper = stepper;
    this._errorStateMatcher = _errorStateMatcher;
    this._destroyed = new Subject();
    this.wasCompleted = false;
    this.interacted = false;
  }
  /** Custom error state matcher that checks for validity of the step form. */
  isErrorState(control, form) {
    const originalErrorState = this._errorStateMatcher.isErrorState(control, form);
    // Checks for the validity of a step form that is not submitted or touched,
    // e.g when the user directly clicks the "next" button or directly on the step
    const customErrorState = !!(control?.invalid && this.interacted);
    return originalErrorState || customErrorState;
  }
  ngOnChanges() {
    // We can't use the `changes: SimpleChanges` as a parameter here
    // because CdkStep only defines the ngOnChanges() method.
    super.ngOnChanges();
    if (this.stepControl !== this._stepControl) {
      this._stepControl = this.stepControl;
      // If a step control changes its state, the stepper needs to update.
      if (this._stepControl) {
        this._stepControl.statusChanges.pipe(takeWhile(() => this._stepControl === this.stepControl), takeUntil(this._destroyed)).subscribe(status => {
          if (this.wasCompleted && status === 'DISABLED') {
            this.stepper._stateChanged();
          } else {
            this.wasCompleted = status === 'VALID';
            this.stepper._stateChanged();
          }
        });
      }
    }
    if (this.interacted !== this._interacted) {
      this._interacted = this.interacted;
      if (this.stepper) {
        this.stepper._stateChanged();
      }
    }
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }
  static {
    this.ɵfac = function NxStepComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxStepComponent)(i0.ɵɵdirectiveInject(forwardRef(() => NxProgressStepperDirective)), i0.ɵɵdirectiveInject(i1.ErrorStateMatcher, 4));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxStepComponent,
      selectors: [["nx-step"]],
      exportAs: ["nxStep"],
      features: [i0.ɵɵProvidersFeature([{
        provide: ErrorStateMatcher,
        useExisting: NxStepComponent
      }]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NxStepComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, NxStepComponent_ng_template_0_Template, 1, 0, "ng-template");
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxStepComponent, [{
    type: Component,
    args: [{
      selector: 'nx-step',
      exportAs: 'nxStep',
      providers: [{
        provide: ErrorStateMatcher,
        useExisting: NxStepComponent
      }],
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: true,
      template: "<ng-template><ng-content></ng-content></ng-template>\n"
    }]
  }], () => [{
    type: NxProgressStepperDirective,
    decorators: [{
      type: Inject,
      args: [forwardRef(() => NxProgressStepperDirective)]
    }]
  }, {
    type: i1.ErrorStateMatcher,
    decorators: [{
      type: SkipSelf
    }]
  }], null);
})();
/** @docs-private */
class NxProgressStepperDirective extends CdkStepper {
  constructor(_cdr, _dir, _elementRef) {
    super(_dir, _cdr, _elementRef);
    this._cdr = _cdr;
    /** Steps that belong to the current stepper, excluding ones from nested steppers. */
    this.steps = new QueryList();
    this._stepHeader = new QueryList();
  }
  ngAfterContentInit() {
    super.ngAfterContentInit();
    // Mark the component for change detection whenever the content children query changes
    this.steps.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this._stateChanged();
    });
    // If a step control changes its state, the stepper needs to update.
    this.steps.filter(s => !!s.stepControl).forEach(step => {
      step.stepControl.statusChanges.pipe(takeUntil(step._destroyed)).subscribe(() => {
        this._stateChanged();
      });
    });
    // we need to defer change detection that the nxStepperNext and NxStepperPrevious buttons
    // can run change detection again, because they are initialized before the stepper content
    Promise.resolve().then(() => {
      this._cdr.detectChanges();
    });
  }
  /** @docs-private */
  get hasPrevious() {
    return this.selectedIndex - 1 >= 0;
  }
  /** @docs-private */
  get hasNext() {
    return this.selectedIndex + 1 < this.count;
  }
  /** @docs-private */
  get count() {
    return this.steps ? this.steps.length : 0;
  }
  /** @docs-private */
  get currentStep() {
    if (this.count === 0 || this.selectedIndex === -1 || !this.steps) {
      return null;
    }
    const steps = this.steps.toArray();
    return steps[this.selectedIndex];
  }
  /** @docs-private */
  get nextStep() {
    if (this.selectedIndex + 1 >= this.steps.length) {
      return null;
    }
    return this.steps.toArray()[this.selectedIndex + 1];
  }
  /** @docs-private */
  get currentLabel() {
    const step = this.currentStep;
    if (!step) {
      return '';
    }
    const label = step.stepLabel || step.label;
    return this.currentStepLabel && label ? `${this.currentStepLabel} ${this.selectedIndex + 1}/${this.count}: ${label}` : '';
  }
  static {
    this.ɵfac = function NxProgressStepperDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxProgressStepperDirective)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.Directionality, 8), i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxProgressStepperDirective,
      selectors: [["", "nxProgressStepper", ""]],
      contentQueries: function NxProgressStepperDirective_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxStepComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._steps = _t);
        }
      },
      inputs: {
        currentStepLabel: "currentStepLabel"
      },
      exportAs: ["nxProgressStepper"],
      features: [i0.ɵɵProvidersFeature([{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxProgressStepperDirective, [{
    type: Directive,
    args: [{
      selector: '[nxProgressStepper]',
      exportAs: 'nxProgressStepper',
      standalone: true,
      providers: [{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1$1.Directionality,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ElementRef
  }], {
    _steps: [{
      type: ContentChildren,
      args: [NxStepComponent, {
        descendants: true
      }]
    }],
    currentStepLabel: [{
      type: Input
    }]
  });
})();
class NxStepperNextDirective extends CdkStepperNext {
  constructor(_stepper, _cdr) {
    super(_stepper);
    this._cdr = _cdr;
    /** @docs-private */
    this.disabled = false;
  }
  /**
   * Implemented to prevent changed after checked error after stepper init.
   * When the stepper initializes the directive is checked first before
   * the stepper can check its content thus it doesn't know about it's children yet
   * so _stepper.hasNext returns false and disables the buttons. In the same CD cycle
   * after the stepper has checked its contents the button needs to be enabled resulting
   * in the error.
   */
  ngDoCheck() {
    const stepper = this._stepper;
    if (this.disabled !== !stepper.hasNext) {
      this.disabled = !stepper.hasNext;
      this._cdr.markForCheck();
    }
  }
  static {
    this.ɵfac = function NxStepperNextDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxStepperNextDirective)(i0.ɵɵdirectiveInject(i1$2.CdkStepper), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxStepperNextDirective,
      selectors: [["button", "nxStepperNext", ""]],
      hostVars: 2,
      hostBindings: function NxStepperNextDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵhostProperty("type", ctx.type)("disabled", ctx.disabled);
        }
      },
      inputs: {
        type: "type"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxStepperNextDirective, [{
    type: Directive,
    args: [{
      selector: 'button[nxStepperNext]',
      host: {
        '[type]': 'type',
        '[disabled]': 'disabled'
      },
      inputs: ['type'],
      providers: [{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }],
      standalone: true
    }]
  }], () => [{
    type: i1$2.CdkStepper
  }, {
    type: i0.ChangeDetectorRef
  }], null);
})();
class NxStepperPreviousDirective extends CdkStepperPrevious {
  constructor(_stepper) {
    super(_stepper);
  }
  get stepper() {
    return this._stepper;
  }
  static {
    this.ɵfac = function NxStepperPreviousDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxStepperPreviousDirective)(i0.ɵɵdirectiveInject(i1$2.CdkStepper));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxStepperPreviousDirective,
      selectors: [["button", "nxStepperPrevious", ""]],
      hostVars: 2,
      hostBindings: function NxStepperPreviousDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵhostProperty("type", ctx.type)("disabled", !ctx.stepper.hasPrevious);
        }
      },
      inputs: {
        type: "type"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxStepperPreviousDirective, [{
    type: Directive,
    args: [{
      selector: 'button[nxStepperPrevious]',
      host: {
        '[type]': 'type',
        '[disabled]': '!stepper.hasPrevious'
      },
      inputs: ['type'],
      providers: [{
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }],
      standalone: true
    }]
  }], () => [{
    type: i1$2.CdkStepper
  }], null);
})();
class NxMultiStepperGroupComponent {
  /** Sets the label of a group that is shown to the user. */
  set label(value) {
    this._label = value;
    this._cdr.markForCheck();
  }
  get label() {
    return this._label;
  }
  constructor(_cdr) {
    this._cdr = _cdr;
  }
  static {
    this.ɵfac = function NxMultiStepperGroupComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxMultiStepperGroupComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxMultiStepperGroupComponent,
      selectors: [["nx-step-group"]],
      contentQueries: function NxMultiStepperGroupComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxStepComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.steps = _t);
        }
      },
      inputs: {
        label: "label"
      },
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NxMultiStepperGroupComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, NxMultiStepperGroupComponent_ng_template_0_Template, 1, 0, "ng-template");
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxMultiStepperGroupComponent, [{
    type: Component,
    args: [{
      selector: 'nx-step-group',
      changeDetection: ChangeDetectionStrategy.OnPush,
      standalone: true,
      template: "<ng-template><ng-content></ng-content></ng-template>\n"
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    steps: [{
      type: ContentChildren,
      args: [NxStepComponent, {
        descendants: true
      }]
    }],
    label: [{
      type: Input
    }]
  });
})();

/** @docs-private */
class NxMultiStepItemComponent extends CdkStepHeader {
  /** The direction of the step */
  set direction(value) {
    this._direction = value;
    this._cdr.markForCheck();
  }
  get direction() {
    return this._direction;
  }
  /** Sets the selected step. */
  set selected(value) {
    this._selected = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get selected() {
    return this._selected;
  }
  /** Sets the active step. */
  set active(value) {
    this._active = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get active() {
    return this._active;
  }
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get disabled() {
    return this._disabled;
  }
  /** Sets the last step. */
  set last(value) {
    this._last = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get last() {
    return this._last;
  }
  /** Sets the step completed. */
  set completed(value) {
    this._completed = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get completed() {
    return this._completed;
  }
  /** Sets the step was completed. */
  set wasCompleted(value) {
    this._wasCompleted = coerceBooleanProperty(value);
  }
  get wasCompleted() {
    return this._wasCompleted;
  }
  set ariaControls(value) {
    this._ariaControls = value;
  }
  get ariaControls() {
    return this._ariaControls;
  }
  constructor(_cdr, _elementRef, _focusMonitor) {
    super(_elementRef);
    this._cdr = _cdr;
    this._elementRef = _elementRef;
    this._focusMonitor = _focusMonitor;
    this._direction = 'horizontal';
  }
  ngAfterViewInit() {
    this._focusMonitor.monitor(this._elementRef);
  }
  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }
  static {
    this.ɵfac = function NxMultiStepItemComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxMultiStepItemComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$3.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxMultiStepItemComponent,
      selectors: [["nx-multi-step-item"]],
      hostAttrs: ["role", "tab"],
      hostVars: 16,
      hostBindings: function NxMultiStepItemComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-disabled", ctx.disabled ? "true" : null)("aria-controls", ctx.ariaControls)("aria-selected", ctx.selected)("aria-label", ctx.label);
          i0.ɵɵclassProp("nx-multi-step-item--vertical", ctx.direction === "vertical")("is-selected", ctx.selected)("is-completed", ctx.completed)("is-active", ctx.active)("is-disabled", ctx.disabled)("is-last", ctx.last);
        }
      },
      inputs: {
        direction: "direction",
        label: "label",
        selected: "selected",
        active: "active",
        disabled: "disabled",
        last: "last",
        completed: "completed",
        wasCompleted: "wasCompleted",
        ariaControls: "ariaControls"
      },
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 6,
      vars: 4,
      consts: [[1, "label-box"], [1, "dot"], [1, "small-dot"], ["name", "check"], [1, "label"], [1, "line"]],
      template: function NxMultiStepItemComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
          i0.ɵɵtemplate(2, NxMultiStepItemComponent_Conditional_2_Template, 1, 0, "div", 2)(3, NxMultiStepItemComponent_Conditional_3_Template, 1, 0, "nx-icon", 3);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(4, NxMultiStepItemComponent_Conditional_4_Template, 2, 2, "div", 4);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(5, NxMultiStepItemComponent_Conditional_5_Template, 1, 0, "div", 5);
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.selected ? 2 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.wasCompleted && !ctx.selected || ctx.completed && !ctx.disabled && !ctx.selected ? 3 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.direction === "vertical" ? 4 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(!ctx.last ? 5 : -1);
        }
      },
      dependencies: [NxIconModule, i2.NxIconComponent],
      styles: ["[_nghost-%COMP%]{flex:1 1 100%;display:flex;align-items:center;justify-content:center;position:relative;padding-bottom:8px;cursor:pointer;max-width:100%}[_nghost-%COMP%]:focus{outline:none}.cdk-keyboard-focused[_nghost-%COMP%]   .dot[_ngcontent-%COMP%]{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.cdk-keyboard-focused[_nghost-%COMP%]   .dot[_ngcontent-%COMP%]{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}@media screen and (forced-colors: active){[_nghost-%COMP%]   .dot[_ngcontent-%COMP%]{border-color:buttonText}[_nghost-%COMP%]   .small-dot[_ngcontent-%COMP%]{background-color:buttonText}}.is-disabled[_nghost-%COMP%]{cursor:not-allowed}.is-disabled[_nghost-%COMP%]   .dot[_ngcontent-%COMP%]{border-color:var(--multi-stepper-dot-disabled-color)}@media screen and (forced-colors: active){.is-disabled[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{color:GrayText}.is-disabled[_nghost-%COMP%]   .dot[_ngcontent-%COMP%]{border-color:GrayText}}.is-completed[_nghost-%COMP%]:not(.is-disabled):not(.is-selected)   .dot[_ngcontent-%COMP%]{background-color:var(--multi-stepper-dot-touched-color)}.nx-multi-step-item--vertical[_nghost-%COMP%]{flex:0 0 auto;flex-direction:column;align-items:flex-start;padding:0}.nx-multi-step-item--vertical[_nghost-%COMP%]   .label-box[_ngcontent-%COMP%]{display:flex;align-items:center}.nx-multi-step-item--vertical[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{position:static;transform:none;margin-left:16px;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;text-align:left;min-width:0;font-size:var(--multi-stepper-step-font-size);line-height:var(--multi-stepper-step-line-height);font-weight:var(--multi-stepper-step-font-weight);letter-spacing:var(--multi-stepper-step-letter-spacing)}[dir=rtl]   .nx-multi-step-item--vertical[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{margin-right:16px;margin-left:initial}.nx-multi-step-item--vertical[_nghost-%COMP%]   .label[_ngcontent-%COMP%]:before{content:attr(label);height:0;overflow:hidden;display:block}@media screen and (forced-colors: active){.nx-multi-step-item--vertical[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{forced-colors-adjust:none;background-color:buttonFace;border-radius:4px;box-shadow:0 0 0 2px buttonFace;color:buttonText}}.nx-multi-step-item--vertical.is-disabled[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{color:var(--multi-stepper-step-disabled-color)}@media screen and (forced-colors: active){.nx-multi-step-item--vertical.is-disabled[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{color:GrayText}}.nx-multi-step-item--vertical.is-selected[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{font-weight:700}@media screen and (forced-colors: active){.nx-multi-step-item--vertical.is-selected[_nghost-%COMP%]   .label[_ngcontent-%COMP%]{background-color:highlight;border-radius:4px;box-shadow:0 0 0 2px highlight;color:highlightText;forced-color-adjust:none}}.nx-multi-step-item--vertical[_nghost-%COMP%]   .line[_ngcontent-%COMP%]{border-top:0;border-left-width:4px;border-left-style:dotted;border-left-color:var(--multi-stepper-line-color);height:16px;flex:0 0 auto;margin-left:6px}[dir=rtl]   .nx-multi-step-item--vertical[_nghost-%COMP%]   .line[_ngcontent-%COMP%]{border-right-width:4px;border-right-style:dotted;border-right-color:var(--multi-stepper-line-color);margin-right:6px;margin-left:initial;border-left:none}.nx-multi-step-item--vertical.is-active[_nghost-%COMP%]   .line[_ngcontent-%COMP%]{border-left-style:solid;border-left-color:var(--multi-stepper-line-filled-color);margin-top:-5px;margin-bottom:-5px;height:26px}[dir=rtl]   .nx-multi-step-item--vertical.is-active[_nghost-%COMP%]   .line[_ngcontent-%COMP%]{border-right-style:solid;border-right-color:var(--multi-stepper-line-filled-color);border-left:none}@media screen and (forced-colors: active){.nx-multi-step-item--vertical.is-active[_nghost-%COMP%]   .line[_ngcontent-%COMP%]{border-color:CanvasText}}.nx-multi-step-item--vertical.is-last[_nghost-%COMP%]{flex:0 0 auto}[_nghost-%COMP%]:not(.nx-multi-step-item--vertical)   .line[_ngcontent-%COMP%]{border-top-width:4px;border-top-style:dotted;border-top-color:var(--multi-stepper-line-color);margin:0 4px;position:absolute;left:20px;width:calc(100% - 24px);transform:translate(50%);top:6px}[dir=rtl]   [_nghost-%COMP%]:not(.nx-multi-step-item--vertical)   .line[_ngcontent-%COMP%]{right:20px;left:auto;transform:translate(-50%)}[_nghost-%COMP%]:not(.nx-multi-step-item--vertical).is-active   .line[_ngcontent-%COMP%]{border-top-color:var(--multi-stepper-line-filled-color);border-top-style:solid;margin:0;left:15px;width:calc(100% - 14px)}[dir=rtl]   [_nghost-%COMP%]:not(.nx-multi-step-item--vertical).is-active   .line[_ngcontent-%COMP%]{right:15px;left:auto}[_nghost-%COMP%]:not(:first-child):before{content:\"\"}.nx-multi-step-item--active[_nghost-%COMP%]:after{border-top-color:var(--multi-stepper-line-filled-color);border-top-style:solid;border-image-source:none;transition:border-style .1s ease-in;margin:0;left:8px;width:calc(100% - 4px)}[dir=rtl]   .nx-multi-step-item--active[_nghost-%COMP%]:after{right:8px;left:auto}.label-box[_ngcontent-%COMP%]{outline:none;appearance:none;border:none;padding:0;margin:0;background:transparent;max-width:100%}.label[_ngcontent-%COMP%]{position:absolute;top:24px;text-align:center;color:var(--multi-stepper-step-color);left:0;width:100%}[dir=rtl][_nghost-%COMP%]   .label[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .label[_ngcontent-%COMP%]{right:0;left:auto}.dot[_ngcontent-%COMP%]{width:16px;height:16px;border-width:2px;border-style:solid;border-color:var(--multi-stepper-dot-touched-color);color:var(--multi-stepper-dot-checkmark-color);background:var(--multi-stepper-dot-background-color);border-radius:50%;flex:0 0 auto;position:relative}.dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{font-size:16px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}[dir=rtl][_nghost-%COMP%]   .dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .dot[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{right:50%;left:auto;transform:translate(50%,-50%)}.small-dot[_ngcontent-%COMP%]{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:8px;height:8px;border-radius:50%;background-color:var(--multi-stepper-dot-touched-color)}[dir=rtl][_nghost-%COMP%]   .small-dot[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .small-dot[_ngcontent-%COMP%]{right:50%;left:auto;transform:translate(50%,-50%)}@media screen and (forced-colors: active){.small-dot[_ngcontent-%COMP%]{background-color:CanvasText}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxMultiStepItemComponent, [{
    type: Component,
    args: [{
      selector: 'nx-multi-step-item',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        '[class.nx-multi-step-item--vertical]': 'direction === "vertical"',
        '[class.is-selected]': 'selected',
        '[class.is-completed]': 'completed',
        '[class.is-active]': 'active',
        '[class.is-disabled]': 'disabled',
        '[class.is-last]': 'last',
        '[attr.aria-disabled]': 'disabled ? "true" : null',
        role: 'tab',
        '[attr.aria-controls]': 'ariaControls',
        '[attr.aria-selected]': 'selected',
        '[attr.aria-label]': 'label'
      },
      imports: [NxIconModule],
      template: "<div class=\"label-box\">\n    <div class=\"dot\">\n        @if (selected) {\n            <div class=\"small-dot\"></div>\n        }\n        @if ((wasCompleted && !selected) || (completed && !disabled && !selected)) {\n            <nx-icon name=\"check\"></nx-icon>\n        }\n    </div>\n    @if (direction === 'vertical') {\n        <div class=\"label\" [attr.label]=\"label\">{{ label }}</div>\n    }\n</div>\n\n@if (!last) {\n    <div class=\"line\"></div>\n}\n",
      styles: [":host{flex:1 1 100%;display:flex;align-items:center;justify-content:center;position:relative;padding-bottom:8px;cursor:pointer;max-width:100%}:host:focus{outline:none}:host.cdk-keyboard-focused .dot{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){:host.cdk-keyboard-focused .dot{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}@media screen and (forced-colors: active){:host .dot{border-color:buttonText}:host .small-dot{background-color:buttonText}}:host.is-disabled{cursor:not-allowed}:host.is-disabled .dot{border-color:var(--multi-stepper-dot-disabled-color)}@media screen and (forced-colors: active){:host.is-disabled .label{color:GrayText}:host.is-disabled .dot{border-color:GrayText}}:host.is-completed:not(.is-disabled):not(.is-selected) .dot{background-color:var(--multi-stepper-dot-touched-color)}:host.nx-multi-step-item--vertical{flex:0 0 auto;flex-direction:column;align-items:flex-start;padding:0}:host.nx-multi-step-item--vertical .label-box{display:flex;align-items:center}:host.nx-multi-step-item--vertical .label{position:static;transform:none;margin-left:16px;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;text-align:left;min-width:0;font-size:var(--multi-stepper-step-font-size);line-height:var(--multi-stepper-step-line-height);font-weight:var(--multi-stepper-step-font-weight);letter-spacing:var(--multi-stepper-step-letter-spacing)}[dir=rtl] :host.nx-multi-step-item--vertical .label{margin-right:16px;margin-left:initial}:host.nx-multi-step-item--vertical .label:before{content:attr(label);height:0;overflow:hidden;display:block}@media screen and (forced-colors: active){:host.nx-multi-step-item--vertical .label{forced-colors-adjust:none;background-color:buttonFace;border-radius:4px;box-shadow:0 0 0 2px buttonFace;color:buttonText}}:host.nx-multi-step-item--vertical.is-disabled .label{color:var(--multi-stepper-step-disabled-color)}@media screen and (forced-colors: active){:host.nx-multi-step-item--vertical.is-disabled .label{color:GrayText}}:host.nx-multi-step-item--vertical.is-selected .label{font-weight:700}@media screen and (forced-colors: active){:host.nx-multi-step-item--vertical.is-selected .label{background-color:highlight;border-radius:4px;box-shadow:0 0 0 2px highlight;color:highlightText;forced-color-adjust:none}}:host.nx-multi-step-item--vertical .line{border-top:0;border-left-width:4px;border-left-style:dotted;border-left-color:var(--multi-stepper-line-color);height:16px;flex:0 0 auto;margin-left:6px}[dir=rtl] :host.nx-multi-step-item--vertical .line{border-right-width:4px;border-right-style:dotted;border-right-color:var(--multi-stepper-line-color);margin-right:6px;margin-left:initial;border-left:none}:host.nx-multi-step-item--vertical.is-active .line{border-left-style:solid;border-left-color:var(--multi-stepper-line-filled-color);margin-top:-5px;margin-bottom:-5px;height:26px}[dir=rtl] :host.nx-multi-step-item--vertical.is-active .line{border-right-style:solid;border-right-color:var(--multi-stepper-line-filled-color);border-left:none}@media screen and (forced-colors: active){:host.nx-multi-step-item--vertical.is-active .line{border-color:CanvasText}}:host.nx-multi-step-item--vertical.is-last{flex:0 0 auto}:host:not(.nx-multi-step-item--vertical) .line{border-top-width:4px;border-top-style:dotted;border-top-color:var(--multi-stepper-line-color);margin:0 4px;position:absolute;left:20px;width:calc(100% - 24px);transform:translate(50%);top:6px}[dir=rtl] :host:not(.nx-multi-step-item--vertical) .line{right:20px;left:auto;transform:translate(-50%)}:host:not(.nx-multi-step-item--vertical).is-active .line{border-top-color:var(--multi-stepper-line-filled-color);border-top-style:solid;margin:0;left:15px;width:calc(100% - 14px)}[dir=rtl] :host:not(.nx-multi-step-item--vertical).is-active .line{right:15px;left:auto}:host:not(:first-child):before{content:\"\"}:host.nx-multi-step-item--active:after{border-top-color:var(--multi-stepper-line-filled-color);border-top-style:solid;border-image-source:none;transition:border-style .1s ease-in;margin:0;left:8px;width:calc(100% - 4px)}[dir=rtl] :host.nx-multi-step-item--active:after{right:8px;left:auto}.label-box{outline:none;appearance:none;border:none;padding:0;margin:0;background:transparent;max-width:100%}.label{position:absolute;top:24px;text-align:center;color:var(--multi-stepper-step-color);left:0;width:100%}:host-context([dir=rtl]) .label{right:0;left:auto}.dot{width:16px;height:16px;border-width:2px;border-style:solid;border-color:var(--multi-stepper-dot-touched-color);color:var(--multi-stepper-dot-checkmark-color);background:var(--multi-stepper-dot-background-color);border-radius:50%;flex:0 0 auto;position:relative}.dot nx-icon{font-size:16px;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}:host-context([dir=rtl]) .dot nx-icon{right:50%;left:auto;transform:translate(50%,-50%)}.small-dot{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:8px;height:8px;border-radius:50%;background-color:var(--multi-stepper-dot-touched-color)}:host-context([dir=rtl]) .small-dot{right:50%;left:auto;transform:translate(50%,-50%)}@media screen and (forced-colors: active){.small-dot{background-color:CanvasText}}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i0.ElementRef
  }, {
    type: i1$3.FocusMonitor
  }], {
    direction: [{
      type: Input
    }],
    label: [{
      type: Input
    }],
    selected: [{
      type: Input
    }],
    active: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    last: [{
      type: Input
    }],
    completed: [{
      type: Input
    }],
    wasCompleted: [{
      type: Input
    }],
    ariaControls: [{
      type: Input
    }]
  });
})();
class NxMultiStepperComponent extends NxProgressStepperDirective {
  /** Sets the direction of the multi stepper. */
  set direction(value) {
    this._direction = value;
  }
  get direction() {
    return this._direction;
  }
  constructor(_cdr, _dir, _el) {
    super(_cdr, _dir, _el);
    this._direction = 'horizontal';
  }
  ngAfterViewChecked() {
    // the _keyManager is currently private in the CdkStepper and the CDK/Material way
    // is to create to separate components for the vertical and horizontal stepper with no
    // possibility to change it during runtime
    // opened a ticket: https://github.com/angular/components/issues/21874
    // for now the quick solution is the hacky way to access the private property
    // eslint-disable-next-line @typescript-eslint/dot-notation
    this['_keyManager'].withVerticalOrientation(this._direction === 'vertical'); // workaround: accessing private class member
  }
  ngAfterContentInit() {
    super.ngAfterContentInit();
    if (this.groups.length) {
      this.steps.reset(this._stepsInGroups);
      this.steps.notifyOnChanges();
    }
    this.groups.changes.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this.steps.reset(this._stepsInGroups);
      this.steps.notifyOnChanges();
    });
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }
  _stepDisabled(index) {
    const steps = this.steps.toArray();
    if (this.linear && index >= 0) {
      return steps.slice(0, index).some(step => {
        const control = step.stepControl;
        const isIncomplete = control ? control.invalid || control.pending || !step.interacted : !step.completed;
        return isIncomplete && !step.optional;
      });
    }
    return false;
  }
  get _stepsInGroups() {
    if (this.groups.length) {
      return this.groups.reduce((steps, group) => steps.concat(group.steps.toArray()), []);
    }
    return [];
  }
  // Returns the index of a certain step
  _getIndex(step) {
    return this.steps.toArray().indexOf(step);
  }
  _isGroupActive(groupIndex) {
    return this.selectedIndex >= this.getGroupItemIndex(groupIndex);
  }
  _isGroupDisabled(groupIndex) {
    return this._stepDisabled(this.getGroupItemIndex(groupIndex));
  }
  getGroupItemIndex(groupIndex) {
    let count = 0;
    const groups = this.groups.toArray();
    for (let i = 0; i < groupIndex; i++) {
      count += groups[i].steps.length;
    }
    return count;
  }
  static {
    this.ɵfac = function NxMultiStepperComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxMultiStepperComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.Directionality, 8), i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxMultiStepperComponent,
      selectors: [["nx-multi-stepper"]],
      contentQueries: function NxMultiStepperComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxMultiStepperGroupComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.groups = _t);
        }
      },
      viewQuery: function NxMultiStepperComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(NxMultiStepItemComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._stepHeader = _t);
        }
      },
      hostVars: 2,
      hostBindings: function NxMultiStepperComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("nx-multi-stepper--vertical", ctx.direction === "vertical");
        }
      },
      inputs: {
        direction: "direction"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxProgressStepperDirective,
        useExisting: NxMultiStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c2,
      decls: 9,
      vars: 4,
      consts: [[1, "nx-progress-stepper__title"], ["role", "tablist", 1, "nx-multi-stepper__items"], [1, "labels-container"], [1, "nx-progress-stepper__mobile-label"], ["role", "tabpanel", 1, "nx-progress-stepper__content", 3, "id"], [3, "last", "tabindex", "label", "completed", "selected", "active", "disabled", "direction", "ariaControls", "wasCompleted"], [3, "click", "keydown", "last", "tabindex", "label", "completed", "selected", "active", "disabled", "direction", "ariaControls", "wasCompleted"], [1, "nx-multi-stepper__group"], [1, "nx-multi-stepper__group-label"], [1, "nx-multi-stepper__group-items"], [3, "tabindex", "last", "label", "completed", "selected", "active", "disabled", "direction", "wasCompleted", "ariaControls"], [3, "click", "keydown", "tabindex", "last", "label", "completed", "selected", "active", "disabled", "direction", "wasCompleted", "ariaControls"], ["tabindex", "-1", 1, "label", 3, "ngClass"], ["tabindex", "-1", 1, "label", 3, "click", "ngClass"], [3, "ngTemplateOutlet"]],
      template: function NxMultiStepperComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵtemplate(0, NxMultiStepperComponent_Conditional_0_Template, 2, 0, "span", 0);
          i0.ɵɵelementStart(1, "div", 1);
          i0.ɵɵtemplate(2, NxMultiStepperComponent_Conditional_2_Template, 2, 0)(3, NxMultiStepperComponent_Conditional_3_Template, 2, 0);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(4, NxMultiStepperComponent_Conditional_4_Template, 3, 0, "div", 2);
          i0.ɵɵelementStart(5, "div", 3);
          i0.ɵɵtext(6);
          i0.ɵɵelementEnd();
          i0.ɵɵrepeaterCreate(7, NxMultiStepperComponent_For_8_Template, 2, 4, "div", 4, i0.ɵɵrepeaterTrackByIdentity);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.direction === "horizontal" ? 0 : -1);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.groups.length === 0 || ctx.direction === "horizontal" ? 2 : 3);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.direction === "horizontal" ? 4 : -1);
          i0.ɵɵadvance(2);
          i0.ɵɵtextInterpolate1(" ", ctx.currentLabel, "\n");
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx.steps);
        }
      },
      dependencies: [NxMultiStepItemComponent, NgClass, NgTemplateOutlet],
      styles: ["[_nghost-%COMP%]{display:block}.nx-progress-stepper__content[aria-expanded=false][_ngcontent-%COMP%]{height:0;overflow:hidden;visibility:hidden}.nx-progress-stepper__mobile-label[_ngcontent-%COMP%]{display:none;color:var(--progress-indicator-mobile-color);font-size:var(--progress-indicator-mobile-font-size);line-height:var(--progress-indicator-mobile-line-height);font-weight:var(--progress-indicator-mobile-font-weight);letter-spacing:var(--progress-indicator-mobile-letter-spacing)}@media (max-width: 703px){.nx-progress-stepper__mobile-label[_ngcontent-%COMP%]{display:block;text-align:center}}.nx-progress-stepper__title[_ngcontent-%COMP%]{display:block}.nx-progress-stepper__title[_ngcontent-%COMP%]     nx-label{margin-bottom:24px}", "[_nghost-%COMP%]{width:100%}@media (min-width: 704px){.nx-multi-stepper--vertical[_nghost-%COMP%]{display:flex}}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-progress-stepper__content[aria-expanded=false][_ngcontent-%COMP%]{width:0}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__items[_ngcontent-%COMP%]{align-items:flex-start;flex-direction:column;margin:0 56px 0 0;max-width:100%}[dir=rtl]   .nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__items[_ngcontent-%COMP%]{margin:0 0 0 56px}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group-items[_ngcontent-%COMP%]{flex-direction:column;max-width:100%}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group-label[_ngcontent-%COMP%]{transform:none;border-left:4px solid transparent;margin-bottom:16px;margin-left:6px;padding-left:22px}[dir=rtl]   .nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group-label[_ngcontent-%COMP%]{margin-left:initial;padding-left:initial;margin-right:6px;padding-right:22px}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group[_ngcontent-%COMP%]:nth-child(n+2)   .nx-multi-stepper__group-label[_ngcontent-%COMP%]{padding-top:16px;margin-top:16px;border-left-style:dotted;border-left-color:var(--multi-stepper-line-color)}.nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group[_ngcontent-%COMP%]:nth-child(n+2)   .nx-multi-stepper__group-label.is-active[_ngcontent-%COMP%]{padding-top:36px;padding-bottom:20px;margin-top:-nx-spacer(3xs);margin-bottom:-nx-spacer(3xs);border-left-style:solid;border-left-color:var(--multi-stepper-line-filled-color)}[dir=rtl]   .nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group[_ngcontent-%COMP%]:nth-child(n+2)   .nx-multi-stepper__group-label[_ngcontent-%COMP%]{border-left-style:none;border-right-style:dotted;border-right-color:var(--multi-stepper-line-color)}[dir=rtl]   .nx-multi-stepper--vertical[_nghost-%COMP%]   .nx-multi-stepper__group[_ngcontent-%COMP%]:nth-child(n+2)   .nx-multi-stepper__group-label.is-active[_ngcontent-%COMP%]{border-left-style:none;border-right-style:solid;border-right-color:var(--multi-stepper-line-filled-color)}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]{width:100%;flex:1 1 100%;display:flex;margin:0 auto;vertical-align:top;align-self:flex-start;align-items:flex-start}@media (max-width: 703px){[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]{display:none}}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label[_ngcontent-%COMP%]{flex:1 1 100%;display:flex;align-items:center;justify-content:center;position:relative;font-size:var(--multi-stepper-step-font-size);line-height:var(--multi-stepper-step-line-height);font-weight:var(--multi-stepper-step-font-weight);letter-spacing:var(--multi-stepper-step-letter-spacing);color:var(--multi-stepper-step-color);cursor:pointer;text-align:center}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label.is-disabled[_ngcontent-%COMP%]{color:var(--multi-stepper-step-disabled-color);cursor:not-allowed}@media screen and (forced-colors: active){[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label[_ngcontent-%COMP%]{color:buttonText}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label.is-disabled[_ngcontent-%COMP%]{color:GrayText}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label[_ngcontent-%COMP%]:not(.is-disabled){color:buttonText}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label[_ngcontent-%COMP%]   span[_ngcontent-%COMP%]{forced-color-adjust:none;background-color:buttonFace;border-radius:4px;box-shadow:0 0 0 2px buttonFace}[_nghost-%COMP%]   .labels-container[_ngcontent-%COMP%]   .label.is-selected[_ngcontent-%COMP%]   span[_ngcontent-%COMP%]{background-color:highlight;border-radius:4px;box-shadow:0 0 0 2px highlight;color:highlightText}}.nx-multi-stepper__items[_ngcontent-%COMP%]{display:flex;margin:0 auto;align-items:center}@media (max-width: 703px){.nx-multi-stepper__items[_ngcontent-%COMP%]{display:none}}.nx-multi-stepper__group-items[_ngcontent-%COMP%]{display:flex}.nx-multi-stepper__group[_ngcontent-%COMP%]{display:flex;flex-direction:column;flex:1 1 auto;max-width:100%}.nx-multi-stepper__group-label[_ngcontent-%COMP%]{font-size:var(--multi-stepper-group-font-size);line-height:var(--multi-stepper-group-line-height);font-weight:var(--multi-stepper-group-font-weight);letter-spacing:var(--multi-stepper-group-letter-spacing);color:var(--multi-stepper-group-color)}.nx-multi-stepper__group-label.is-disabled[_ngcontent-%COMP%]{color:var(--multi-stepper-group-disabled-color)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxMultiStepperComponent, [{
    type: Component,
    args: [{
      selector: 'nx-multi-stepper',
      providers: [{
        provide: NxProgressStepperDirective,
        useExisting: NxMultiStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }],
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        '[class.nx-multi-stepper--vertical]': 'direction === "vertical"'
      },
      imports: [NxMultiStepItemComponent, NgClass, NgTemplateOutlet],
      template: "@if (direction === 'horizontal') {\n    <span class=\"nx-progress-stepper__title\">\n        <ng-content select=\"nx-label\"></ng-content>\n    </span>\n}\n\n<div class=\"nx-multi-stepper__items\" role=\"tablist\">\n    @if (groups.length === 0 || direction === 'horizontal') {\n        @for (step of steps; track step; let i = $index; let isLast = $last; let isFirst = $first) {\n            <nx-multi-step-item\n                (click)=\"!_stepDisabled(i) && step.select()\"\n                (keydown)=\"_onKeydown($event)\"\n                [last]=\"isLast\"\n                [tabindex]=\"_getFocusIndex() === i ? 0 : -1\"\n                [label]=\"step.stepLabel || step.label\"\n                [completed]=\"step.completed || step.wasCompleted\"\n                [selected]=\"selectedIndex === i\"\n                [active]=\"selectedIndex > i\"\n                [disabled]=\"_stepDisabled(i)\"\n                [direction]=\"direction\"\n                [ariaControls]=\"_getStepContentId(i)\"\n                [wasCompleted]=\"step.wasCompleted\"\n            >\n            </nx-multi-step-item>\n        }\n    } @else {\n        @for (group of groups; track group; let i = $index) {\n            <div class=\"nx-multi-stepper__group\">\n                <div class=\"nx-multi-stepper__group-label\" [class.is-active]=\"_isGroupActive(i)\" [class.is-disabled]=\"_isGroupDisabled(i)\">\n                    {{ group.label }}\n                </div>\n                <div class=\"nx-multi-stepper__group-items\">\n                    @for (step of group.steps; track step; let isLast = $last) {\n                        <nx-multi-step-item\n                            (click)=\"!_stepDisabled(_getIndex(step)) && step.select()\"\n                            (keydown)=\"_onKeydown($event)\"\n                            [tabindex]=\"_getFocusIndex() === _getIndex(step) ? 0 : -1\"\n                            [last]=\"isLast\"\n                            [label]=\"step.stepLabel || step.label\"\n                            [completed]=\"step.completed || step.wasCompleted\"\n                            [selected]=\"selectedIndex === _getIndex(step)\"\n                            [active]=\"selectedIndex > _getIndex(step)\"\n                            [disabled]=\"_stepDisabled(_getIndex(step))\"\n                            [direction]=\"direction\"\n                            [wasCompleted]=\"step.wasCompleted\"\n                            [ariaControls]=\"_getStepContentId(i)\"\n                        >\n                        </nx-multi-step-item>\n                    }\n                </div>\n            </div>\n        }\n    }\n</div>\n\n@if (direction === 'horizontal') {\n    <div class=\"labels-container\">\n        @for (step of steps; track step; let i = $index) {\n            <div\n                class=\"label\"\n                tabindex=\"-1\"\n                [attr.aria-controls]=\"_getStepContentId(i)\"\n                [ngClass]=\"{ 'is-disabled': _stepDisabled(i), 'is-selected': selectedIndex === i }\"\n                (click)=\"!_stepDisabled(i) && step.select()\"\n            >\n                <span>{{ step.label }}</span>\n            </div>\n        }\n    </div>\n}\n\n<div class=\"nx-progress-stepper__mobile-label\">\n    {{ currentLabel }}\n</div>\n\n@for (step of steps; track step; let i = $index) {\n    <div\n        class=\"nx-progress-stepper__content\"\n        role=\"tabpanel\"\n        [id]=\"_getStepContentId(i)\"\n        [attr.aria-label]=\"currentLabel\"\n        [attr.aria-expanded]=\"selectedIndex === i\"\n    >\n        <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\n    </div>\n}\n",
      styles: [":host{display:block}.nx-progress-stepper__content[aria-expanded=false]{height:0;overflow:hidden;visibility:hidden}.nx-progress-stepper__mobile-label{display:none;color:var(--progress-indicator-mobile-color);font-size:var(--progress-indicator-mobile-font-size);line-height:var(--progress-indicator-mobile-line-height);font-weight:var(--progress-indicator-mobile-font-weight);letter-spacing:var(--progress-indicator-mobile-letter-spacing)}@media (max-width: 703px){.nx-progress-stepper__mobile-label{display:block;text-align:center}}.nx-progress-stepper__title{display:block}.nx-progress-stepper__title ::ng-deep nx-label{margin-bottom:24px}\n", ":host{width:100%}@media (min-width: 704px){:host.nx-multi-stepper--vertical{display:flex}}:host.nx-multi-stepper--vertical .nx-progress-stepper__content[aria-expanded=false]{width:0}:host.nx-multi-stepper--vertical .nx-multi-stepper__items{align-items:flex-start;flex-direction:column;margin:0 56px 0 0;max-width:100%}[dir=rtl] :host.nx-multi-stepper--vertical .nx-multi-stepper__items{margin:0 0 0 56px}:host.nx-multi-stepper--vertical .nx-multi-stepper__group-items{flex-direction:column;max-width:100%}:host.nx-multi-stepper--vertical .nx-multi-stepper__group-label{transform:none;border-left:4px solid transparent;margin-bottom:16px;margin-left:6px;padding-left:22px}[dir=rtl] :host.nx-multi-stepper--vertical .nx-multi-stepper__group-label{margin-left:initial;padding-left:initial;margin-right:6px;padding-right:22px}:host.nx-multi-stepper--vertical .nx-multi-stepper__group:nth-child(n+2) .nx-multi-stepper__group-label{padding-top:16px;margin-top:16px;border-left-style:dotted;border-left-color:var(--multi-stepper-line-color)}:host.nx-multi-stepper--vertical .nx-multi-stepper__group:nth-child(n+2) .nx-multi-stepper__group-label.is-active{padding-top:36px;padding-bottom:20px;margin-top:-nx-spacer(3xs);margin-bottom:-nx-spacer(3xs);border-left-style:solid;border-left-color:var(--multi-stepper-line-filled-color)}[dir=rtl] :host.nx-multi-stepper--vertical .nx-multi-stepper__group:nth-child(n+2) .nx-multi-stepper__group-label{border-left-style:none;border-right-style:dotted;border-right-color:var(--multi-stepper-line-color)}[dir=rtl] :host.nx-multi-stepper--vertical .nx-multi-stepper__group:nth-child(n+2) .nx-multi-stepper__group-label.is-active{border-left-style:none;border-right-style:solid;border-right-color:var(--multi-stepper-line-filled-color)}:host .labels-container{width:100%;flex:1 1 100%;display:flex;margin:0 auto;vertical-align:top;align-self:flex-start;align-items:flex-start}@media (max-width: 703px){:host .labels-container{display:none}}:host .labels-container .label{flex:1 1 100%;display:flex;align-items:center;justify-content:center;position:relative;font-size:var(--multi-stepper-step-font-size);line-height:var(--multi-stepper-step-line-height);font-weight:var(--multi-stepper-step-font-weight);letter-spacing:var(--multi-stepper-step-letter-spacing);color:var(--multi-stepper-step-color);cursor:pointer;text-align:center}:host .labels-container .label.is-disabled{color:var(--multi-stepper-step-disabled-color);cursor:not-allowed}@media screen and (forced-colors: active){:host .labels-container .label{color:buttonText}:host .labels-container .label.is-disabled{color:GrayText}:host .labels-container .label:not(.is-disabled){color:buttonText}:host .labels-container .label span{forced-color-adjust:none;background-color:buttonFace;border-radius:4px;box-shadow:0 0 0 2px buttonFace}:host .labels-container .label.is-selected span{background-color:highlight;border-radius:4px;box-shadow:0 0 0 2px highlight;color:highlightText}}.nx-multi-stepper__items{display:flex;margin:0 auto;align-items:center}@media (max-width: 703px){.nx-multi-stepper__items{display:none}}.nx-multi-stepper__group-items{display:flex}.nx-multi-stepper__group{display:flex;flex-direction:column;flex:1 1 auto;max-width:100%}.nx-multi-stepper__group-label{font-size:var(--multi-stepper-group-font-size);line-height:var(--multi-stepper-group-line-height);font-weight:var(--multi-stepper-group-font-weight);letter-spacing:var(--multi-stepper-group-letter-spacing);color:var(--multi-stepper-group-color)}.nx-multi-stepper__group-label.is-disabled{color:var(--multi-stepper-group-disabled-color)}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1$1.Directionality,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ElementRef
  }], {
    _stepHeader: [{
      type: ViewChildren,
      args: [NxMultiStepItemComponent]
    }],
    groups: [{
      type: ContentChildren,
      args: [NxMultiStepperGroupComponent, {
        descendants: true
      }]
    }],
    direction: [{
      type: Input
    }]
  });
})();
class NxProgressStepperComponent extends NxProgressStepperDirective {
  /** Sets the current value/progress of the progress bar. */
  set progress(value) {
    this._progress = clamp(value || 0);
  }
  get progress() {
    return this._progress;
  }
  constructor(_cdr, _dir, _el) {
    super(_cdr, _dir, _el);
    /** Overrides the `aria-label` of the nx-progressbar. Defaults to "Progress" */
    this.progressbarAriaLabel = 'Progress';
    this._progress = 0;
  }
  static {
    this.ɵfac = function NxProgressStepperComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxProgressStepperComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.Directionality, 8), i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxProgressStepperComponent,
      selectors: [["nx-progress-stepper"]],
      inputs: {
        progressbarAriaLabel: "progressbarAriaLabel",
        progressbarAriaLabeledBy: "progressbarAriaLabeledBy",
        progress: "progress"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxProgressStepperDirective,
        useExisting: NxProgressStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c2,
      decls: 9,
      vars: 1,
      consts: [[1, "nx-progress-stepper__title"], [1, "nx-subprogress__items"], [1, "nx-subprogress__item", 3, "nx-subprogress__item--inactive", "nx-subprogress__item--selected"], [1, "nx-progress-stepper__mobile-label"], ["role", "tabpanel", 1, "nx-progress-stepper__content", 3, "id"], [1, "nx-subprogress__item"], [1, "nx-subprogress__counter"], [1, "nx-subprogress__number"], [1, "nx-subprogress__label"], [1, "nx-subprogress__progress"], [3, "value", "ariaLabel", "ariaLabelledBy"], [3, "ngTemplateOutlet"]],
      template: function NxProgressStepperComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵelementStart(0, "span", 0);
          i0.ɵɵprojection(1);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(2, "div", 1);
          i0.ɵɵrepeaterCreate(3, NxProgressStepperComponent_For_4_Template, 7, 8, "div", 2, i0.ɵɵrepeaterTrackByIdentity);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(5, "div", 3);
          i0.ɵɵtext(6);
          i0.ɵɵelementEnd();
          i0.ɵɵrepeaterCreate(7, NxProgressStepperComponent_For_8_Template, 2, 4, "div", 4, i0.ɵɵrepeaterTrackByIdentity);
        }
        if (rf & 2) {
          i0.ɵɵadvance(3);
          i0.ɵɵrepeater(ctx.steps);
          i0.ɵɵadvance(3);
          i0.ɵɵtextInterpolate1(" ", ctx.currentLabel, "\n");
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx.steps);
        }
      },
      dependencies: [NxProgressbarModule, i2$1.NxProgressbarComponent, NgTemplateOutlet],
      styles: [_c4, ".nx-subprogress__items[_ngcontent-%COMP%]{display:flex;margin:0 auto}@media (max-width: 703px){.nx-subprogress__items[_ngcontent-%COMP%]{display:none}}.nx-subprogress__item[_ngcontent-%COMP%]{display:flex;flex:1 0 auto}.nx-subprogress__item[_ngcontent-%COMP%]:not(:first-child){margin-left:16px}[dir=rtl][_nghost-%COMP%]   .nx-subprogress__item[_ngcontent-%COMP%]:not(:first-child), [dir=rtl]   [_nghost-%COMP%]   .nx-subprogress__item[_ngcontent-%COMP%]:not(:first-child){margin-right:16px;margin-left:initial}.nx-subprogress__item--selected[_ngcontent-%COMP%]{display:flex;flex:1 1 100%;align-items:center}.nx-subprogress__progress[_ngcontent-%COMP%]{flex:1 1 100%;margin-left:8px}[dir=rtl][_nghost-%COMP%]   .nx-subprogress__progress[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-subprogress__progress[_ngcontent-%COMP%]{margin-right:8px;margin-left:initial}.nx-subprogress__item--inactive[_ngcontent-%COMP%]   .nx-subprogress__counter[_ngcontent-%COMP%]{max-width:24px;background-color:transparent;border-width:2px;border-style:solid;border-color:var(--progress-stepper-dot-disabled-background-color);color:var(--progress-stepper-dot-disabled-background-color)}.nx-subprogress__item--inactive[_ngcontent-%COMP%]   .nx-subprogress__label[_ngcontent-%COMP%]{color:var(--progress-stepper-step-disabled-color)}.nx-subprogress__label[_ngcontent-%COMP%]{flex:1 1 auto;white-space:nowrap;text-overflow:ellipsis;color:var(--progress-stepper-step-color);font-size:var(--progress-stepper-step-font-size);line-height:var(--progress-stepper-step-line-height);font-weight:var(--progress-stepper-step-font-weight);letter-spacing:var(--progress-stepper-step-letter-spacing)}.nx-subprogress__counter[_ngcontent-%COMP%]{flex:0 0 24px;display:flex;align-items:center;justify-content:center;background-color:var(--progress-stepper-dot-background-color);color:var(--progress-stepper-dot-color);width:24px;height:24px;text-align:center;border-radius:50%;margin-right:8px}[dir=rtl][_nghost-%COMP%]   .nx-subprogress__counter[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-subprogress__counter[_ngcontent-%COMP%]{margin-right:initial;margin-left:8px}@media screen and (forced-colors: active){.nx-subprogress__counter[_ngcontent-%COMP%]{forced-colors-adjust:none;background-color:transparent;color:CanvasText;border:1px solid CanvasText;border-radius:50%}.nx-subprogress__item--inactive[_ngcontent-%COMP%]{color:GrayText}.nx-subprogress__item--inactive[_ngcontent-%COMP%]   .nx-subprogress__counter[_ngcontent-%COMP%]{color:GrayText;border:1px solid GrayText}}.nx-subprogress__number[_ngcontent-%COMP%]{font-size:var(--progress-stepper-dotnumber-font-size);line-height:var(--progress-stepper-dotnumber-line-height);font-weight:var(--progress-stepper-dotnumber-font-weight);letter-spacing:var(--progress-stepper-dotnumber-letter-spacing)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxProgressStepperComponent, [{
    type: Component,
    args: [{
      selector: 'nx-progress-stepper',
      providers: [{
        provide: NxProgressStepperDirective,
        useExisting: NxProgressStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }],
      changeDetection: ChangeDetectionStrategy.OnPush,
      imports: [NxProgressbarModule, NgTemplateOutlet],
      template: "<span class=\"nx-progress-stepper__title\">\n    <ng-content select=\"nx-label\"></ng-content>\n</span>\n\n<div class=\"nx-subprogress__items\">\n    @for (step of steps; track step; let i = $index) {\n        <div\n            [class.nx-subprogress__item--inactive]=\"selectedIndex !== i && !step.completed\"\n            [attr.aria-disabled]=\"selectedIndex !== i && !step.completed\"\n            [class.nx-subprogress__item--selected]=\"selectedIndex === i\"\n            class=\"nx-subprogress__item\"\n        >\n            <div class=\"nx-subprogress__counter\">\n                <div class=\"nx-subprogress__number\">\n                    {{ i + 1 }}\n                </div>\n            </div>\n            <div class=\"nx-subprogress__label\">{{ step.stepLabel || step.label }}</div>\n            @if (selectedIndex === i) {\n                <div class=\"nx-subprogress__progress\">\n                    <nx-progressbar [value]=\"progress\" [ariaLabel]=\"progressbarAriaLabel\" [ariaLabelledBy]=\"progressbarAriaLabeledBy\"></nx-progressbar>\n                </div>\n            }\n        </div>\n    }\n</div>\n\n<div class=\"nx-progress-stepper__mobile-label\">\n    {{ currentLabel }}\n</div>\n\n@for (step of steps; track step; let i = $index) {\n    <div\n        class=\"nx-progress-stepper__content\"\n        role=\"tabpanel\"\n        [id]=\"_getStepContentId(i)\"\n        [attr.aria-label]=\"currentLabel\"\n        [attr.aria-expanded]=\"selectedIndex === i\"\n    >\n        <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\n    </div>\n}\n",
      styles: [":host{display:block}.nx-progress-stepper__content[aria-expanded=false]{height:0;overflow:hidden;visibility:hidden}.nx-progress-stepper__mobile-label{display:none;color:var(--progress-indicator-mobile-color);font-size:var(--progress-indicator-mobile-font-size);line-height:var(--progress-indicator-mobile-line-height);font-weight:var(--progress-indicator-mobile-font-weight);letter-spacing:var(--progress-indicator-mobile-letter-spacing)}@media (max-width: 703px){.nx-progress-stepper__mobile-label{display:block;text-align:center}}.nx-progress-stepper__title{display:block}.nx-progress-stepper__title ::ng-deep nx-label{margin-bottom:24px}\n", ".nx-subprogress__items{display:flex;margin:0 auto}@media (max-width: 703px){.nx-subprogress__items{display:none}}.nx-subprogress__item{display:flex;flex:1 0 auto}.nx-subprogress__item:not(:first-child){margin-left:16px}:host-context([dir=rtl]) .nx-subprogress__item:not(:first-child){margin-right:16px;margin-left:initial}.nx-subprogress__item--selected{display:flex;flex:1 1 100%;align-items:center}.nx-subprogress__progress{flex:1 1 100%;margin-left:8px}:host-context([dir=rtl]) .nx-subprogress__progress{margin-right:8px;margin-left:initial}.nx-subprogress__item--inactive .nx-subprogress__counter{max-width:24px;background-color:transparent;border-width:2px;border-style:solid;border-color:var(--progress-stepper-dot-disabled-background-color);color:var(--progress-stepper-dot-disabled-background-color)}.nx-subprogress__item--inactive .nx-subprogress__label{color:var(--progress-stepper-step-disabled-color)}.nx-subprogress__label{flex:1 1 auto;white-space:nowrap;text-overflow:ellipsis;color:var(--progress-stepper-step-color);font-size:var(--progress-stepper-step-font-size);line-height:var(--progress-stepper-step-line-height);font-weight:var(--progress-stepper-step-font-weight);letter-spacing:var(--progress-stepper-step-letter-spacing)}.nx-subprogress__counter{flex:0 0 24px;display:flex;align-items:center;justify-content:center;background-color:var(--progress-stepper-dot-background-color);color:var(--progress-stepper-dot-color);width:24px;height:24px;text-align:center;border-radius:50%;margin-right:8px}:host-context([dir=rtl]) .nx-subprogress__counter{margin-right:initial;margin-left:8px}@media screen and (forced-colors: active){.nx-subprogress__counter{forced-colors-adjust:none;background-color:transparent;color:CanvasText;border:1px solid CanvasText;border-radius:50%}.nx-subprogress__item--inactive{color:GrayText}.nx-subprogress__item--inactive .nx-subprogress__counter{color:GrayText;border:1px solid GrayText}}.nx-subprogress__number{font-size:var(--progress-stepper-dotnumber-font-size);line-height:var(--progress-stepper-dotnumber-line-height);font-weight:var(--progress-stepper-dotnumber-font-weight);letter-spacing:var(--progress-stepper-dotnumber-letter-spacing)}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1$1.Directionality,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ElementRef
  }], {
    progressbarAriaLabel: [{
      type: Input
    }],
    progressbarAriaLabeledBy: [{
      type: Input
    }],
    progress: [{
      type: Input
    }]
  });
})();
class NxSingleStepperComponent extends NxProgressStepperDirective {
  /** Sets the label on the right showing the next step. */
  set rightLabel(v) {
    this._rightLabel = v;
  }
  get rightLabel() {
    return this._rightLabel;
  }
  /** @docs-private */
  get progress() {
    return (this.selectedIndex + 1) / this.count;
  }
  /** @docs-private */
  get currentLabel() {
    const step = this.currentStep;
    const label = step.stepLabel || step.label;
    return `
    ${this.currentStepLabel} ${this.selectedIndex + 1}/${this.count}: ${label}
    `;
  }
  /** @docs-private */
  get nextLabel() {
    const step = this.nextStep;
    if (step) {
      const label = step.stepLabel || step.label;
      return `
        ${this.rightLabel} ${label}
      `;
    }
    return null;
  }
  constructor(_cdr, _dir, _el) {
    super(_cdr, _dir, _el);
    this._rightLabel = 'Next step:';
    /** Overrides the `aria-label` of the nx-progressbar. Defaults to "Progress" */
    this.progressbarAriaLabel = 'Progress';
  }
  static {
    this.ɵfac = function NxSingleStepperComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxSingleStepperComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.Directionality, 8), i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxSingleStepperComponent,
      selectors: [["nx-single-stepper"]],
      inputs: {
        rightLabel: [0, "nextLabel", "rightLabel"],
        progressbarAriaLabel: "progressbarAriaLabel",
        progressbarAriaLabeledBy: "progressbarAriaLabeledBy"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxProgressStepperDirective,
        useExisting: NxSingleStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }]), i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c2,
      decls: 13,
      vars: 6,
      consts: [[1, "nx-single-step-container"], [1, "nx-progress-stepper__title"], [3, "value", "ariaLabel", "ariaLabelledBy"], [1, "nx-single-step__infos"], [1, "nx-single-step__current"], [1, "nx-single-step__next"], [1, "nx-progress-stepper__mobile-label"], ["role", "tabpanel", 1, "nx-progress-stepper__content", 3, "id"], [3, "ngTemplateOutlet"]],
      template: function NxSingleStepperComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵelementStart(0, "div", 0)(1, "span", 1);
          i0.ɵɵprojection(2);
          i0.ɵɵelementEnd();
          i0.ɵɵelement(3, "nx-progressbar", 2);
          i0.ɵɵelementStart(4, "div", 3)(5, "div", 4);
          i0.ɵɵtext(6);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(7, "div", 5);
          i0.ɵɵtext(8);
          i0.ɵɵelementEnd()();
          i0.ɵɵelementStart(9, "div", 6);
          i0.ɵɵtext(10);
          i0.ɵɵelementEnd()();
          i0.ɵɵrepeaterCreate(11, NxSingleStepperComponent_For_12_Template, 2, 4, "div", 7, i0.ɵɵrepeaterTrackByIdentity);
        }
        if (rf & 2) {
          i0.ɵɵadvance(3);
          i0.ɵɵproperty("value", ctx.progress)("ariaLabel", ctx.progressbarAriaLabel)("ariaLabelledBy", ctx.progressbarAriaLabeledBy);
          i0.ɵɵadvance(3);
          i0.ɵɵtextInterpolate1(" ", ctx.currentLabel, " ");
          i0.ɵɵadvance(2);
          i0.ɵɵtextInterpolate1(" ", ctx.nextLabel, " ");
          i0.ɵɵadvance(2);
          i0.ɵɵtextInterpolate1(" ", ctx.currentLabel, " ");
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx.steps);
        }
      },
      dependencies: [NxProgressbarModule, i2$1.NxProgressbarComponent, NgTemplateOutlet],
      styles: [_c4, "[_nghost-%COMP%]{display:block}.nx-single-step__infos[_ngcontent-%COMP%]{display:flex;font-size:var(--single-stepper-step-font-size);line-height:var(--single-stepper-step-line-height);font-weight:var(--single-stepper-step-font-weight);letter-spacing:var(--single-stepper-step-letter-spacing);color:var(--single-stepper-step-color);margin-top:8px}nx-progressbar[_ngcontent-%COMP%]{margin-bottom:8px;display:block}@media (max-width: 703px){nx-progressbar[_ngcontent-%COMP%], .nx-single-step__infos[_ngcontent-%COMP%]{display:none}}.nx-single-step__next[_ngcontent-%COMP%]{margin-left:auto}[dir=rtl][_nghost-%COMP%]   .nx-single-step__next[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-single-step__next[_ngcontent-%COMP%]{margin-right:auto;margin-left:initial}@media (max-width: 703px){.nx-single-step__next[_ngcontent-%COMP%]{display:none}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxSingleStepperComponent, [{
    type: Component,
    args: [{
      selector: 'nx-single-stepper',
      providers: [{
        provide: NxProgressStepperDirective,
        useExisting: NxSingleStepperComponent
      }, {
        provide: CdkStepper,
        useExisting: NxProgressStepperDirective
      }],
      changeDetection: ChangeDetectionStrategy.OnPush,
      imports: [NxProgressbarModule, NgTemplateOutlet],
      template: "<div class=\"nx-single-step-container\">\n    <span class=\"nx-progress-stepper__title\">\n        <ng-content select=\"nx-label\"></ng-content>\n    </span>\n\n    <nx-progressbar [value]=\"progress\" [ariaLabel]=\"progressbarAriaLabel\" [ariaLabelledBy]=\"progressbarAriaLabeledBy\"></nx-progressbar>\n\n    <div class=\"nx-single-step__infos\">\n        <div class=\"nx-single-step__current\">\n            {{ currentLabel }}\n        </div>\n        <div class=\"nx-single-step__next\">\n            {{ nextLabel }}\n        </div>\n    </div>\n\n    <div class=\"nx-progress-stepper__mobile-label\">\n        {{ currentLabel }}\n    </div>\n</div>\n\n@for (step of steps; track step; let i = $index) {\n    <div\n        class=\"nx-progress-stepper__content\"\n        role=\"tabpanel\"\n        [id]=\"_getStepContentId(i)\"\n        [attr.aria-label]=\"currentLabel\"\n        [attr.aria-expanded]=\"selectedIndex === i\"\n    >\n        <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\n    </div>\n}\n",
      styles: [":host{display:block}.nx-progress-stepper__content[aria-expanded=false]{height:0;overflow:hidden;visibility:hidden}.nx-progress-stepper__mobile-label{display:none;color:var(--progress-indicator-mobile-color);font-size:var(--progress-indicator-mobile-font-size);line-height:var(--progress-indicator-mobile-line-height);font-weight:var(--progress-indicator-mobile-font-weight);letter-spacing:var(--progress-indicator-mobile-letter-spacing)}@media (max-width: 703px){.nx-progress-stepper__mobile-label{display:block;text-align:center}}.nx-progress-stepper__title{display:block}.nx-progress-stepper__title ::ng-deep nx-label{margin-bottom:24px}\n", ":host{display:block}.nx-single-step__infos{display:flex;font-size:var(--single-stepper-step-font-size);line-height:var(--single-stepper-step-line-height);font-weight:var(--single-stepper-step-font-weight);letter-spacing:var(--single-stepper-step-letter-spacing);color:var(--single-stepper-step-color);margin-top:8px}nx-progressbar{margin-bottom:8px;display:block}@media (max-width: 703px){nx-progressbar,.nx-single-step__infos{display:none}}.nx-single-step__next{margin-left:auto}:host-context([dir=rtl]) .nx-single-step__next{margin-right:auto;margin-left:initial}@media (max-width: 703px){.nx-single-step__next{display:none}}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1$1.Directionality,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ElementRef
  }], {
    rightLabel: [{
      type: Input,
      args: ['nextLabel']
    }],
    progressbarAriaLabel: [{
      type: Input
    }],
    progressbarAriaLabeledBy: [{
      type: Input,
      args: ['progressbarAriaLabeledBy']
    }]
  });
})();
class NxProgressStepperModule {
  static {
    this.ɵfac = function NxProgressStepperModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxProgressStepperModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxProgressStepperModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, CdkStepperModule, NxIconModule, NxProgressbarModule, NxLabelModule, NxMultiStepperComponent, NxProgressStepperComponent, NxSingleStepperComponent, NxMultiStepItemComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxProgressStepperModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, CdkStepperModule, NxIconModule, NxProgressbarModule, NxLabelModule, NxProgressStepperDirective, NxMultiStepperComponent, NxStepComponent, NxStepperNextDirective, NxStepperPreviousDirective, NxProgressStepperComponent, NxSingleStepperComponent, NxMultiStepItemComponent, NxMultiStepperGroupComponent],
      exports: [NxProgressStepperDirective, NxSingleStepperComponent, NxProgressStepperComponent, NxMultiStepperComponent, NxStepComponent, NxStepperNextDirective, NxStepperPreviousDirective, NxMultiStepperGroupComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxMultiStepItemComponent, NxMultiStepperComponent, NxMultiStepperGroupComponent, NxProgressStepperComponent, NxProgressStepperDirective, NxProgressStepperModule, NxSingleStepperComponent, NxStepComponent, NxStepperNextDirective, NxStepperPreviousDirective };
